import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import PubSub from 'pubsub-js';
import React, { useState, useContext } from 'react';
import { CSSTransition } from 'react-transition-group';
import hive from '@bombas/hive';
import { useBreakpoint } from '../../util/Breakpoint.context';
import Icon from '../Icons/Icon';
import SelectTileGroup from '../Product/components/SelectTileGroup/SelectTileGroup.styled';
import { PRODUCT_VARIANT_PIM_DATA } from './QuickAdd.gql';
import * as Styled from './QuickAdd.styled';
import { ContainerQueryContext } from '../../util/ContainerQuery.context';

const TRANSITION_DURATION = 200;

/**
 * Calls addToCart along with custom tracking
 *
 * @param {Object} currentVariant product level data for the selected line item
 * @param {Object} pimData component level data used for QBS
 */
export const handleAddToCart = async ({
  currentVariant,
  pimData = {},
  productId
}) => {
  const { id, category, isPack, packSize } = currentVariant;
  const { endUse, endUse2, primaryMaterial, lifestyle } = pimData;

  hive.track('CART_ADD_PCP');

  PubSub.publish('cart.add', {
    variantId: id,
    quantity: 1,
    pimData: {
      category,
      isPack,
      packSize,
      endUse,
      endUse2,
      lifestyle,
      material: primaryMaterial
    },
    productId,
    source: 'QuickAdd'
  });
};

/**
 * QuickAdd allows for a streamlined way to add to cart within a ProductCard
 */
const QuickAdd = ({
  children,
  className,
  showSeeFullDetails,
  link,
  setForceEmbossShadow,
  showSizeOptions,
  setShowSizeOptions,
  openQuickAddModal,
  productId
}) => {
  const [currentVariant, setCurrentVariant] = useState(null);
  const [getProductVariantPimData] = useLazyQuery(PRODUCT_VARIANT_PIM_DATA, {
    onCompleted: (data) => {
      const pimData = data.productVariants[0].components[0];
      const currentVariantWithSku = {
        ...currentVariant,
        sku: data.productVariants[0].sku
      };
      handleAddToCart({
        currentVariant: currentVariantWithSku,
        pimData,
        productId
      });
    }
  });
  const { styleQueryMode } = useContext(ContainerQueryContext);
  const { medium, large } = useBreakpoint();

  const addToBagHandle = (e) => {
    if (!showSeeFullDetails) {
      e.preventDefault();
      hive.track('QUICKADD_CLICK');
      if (medium && !large && setForceEmbossShadow) {
        setForceEmbossShadow(true);
      }

      // open modal if small breakpoint, otherwise show QuickAdd size options
      if (!medium) {
        openQuickAddModal();
      } else {
        setShowSizeOptions(true);
      }
    }
  };

  return (
    <Styled.QuickAdd className={className} $styleQueryMode={styleQueryMode}>
      <Styled.QuickAddButton
        size="small"
        iconPosition="left"
        variant="defaultLight"
        onClick={addToBagHandle}
        isFull
        className="full-width-quick-add"
        /**
         * If showSeeFullDetails is true, the button is rendered as an anchor tag to navigate the user to a PDP
         */
        as={showSeeFullDetails ? 'a' : 'button'}
        href={showSeeFullDetails ? link : null}
        $showSizeOptions={!showSizeOptions}
        $styleQueryMode={styleQueryMode}
        data-label="productCard_quickAdd_L1"
      >
        {showSeeFullDetails ? (
          'See Full Details'
        ) : (
          <>
            <Icon style={{ marginRight: '4px' }} name="plus" />
            Add to Bag
          </>
        )}
      </Styled.QuickAddButton>

      <CSSTransition
        mountOnEnter
        in={showSizeOptions}
        classNames="quick-add"
        timeout={{ enter: TRANSITION_DURATION, exit: 0 }}
        unmountOnExit
      >
        <Styled.Options
          transitionDuration={TRANSITION_DURATION}
          $showSizeOptions={showSizeOptions}
          $styleQueryMode={styleQueryMode}
        >
          <SelectTileGroup tiles={React.Children.count(children)}>
            {React.Children.map(
              children,
              (child) =>
                child &&
                React.cloneElement(child, {
                  onClick: async () => {
                    getProductVariantPimData({
                      variables: { ids: [child.props.id] }
                    });
                    setCurrentVariant(child.props);

                    setShowSizeOptions(false);
                    if (medium && setForceEmbossShadow)
                      setForceEmbossShadow(false);
                  }
                })
            )}
          </SelectTileGroup>
        </Styled.Options>
      </CSSTransition>
    </Styled.QuickAdd>
  );
};

QuickAdd.propTypes = {
  /**
   * An array of ProducttOptionTiles passed as children
   */
  children: PropTypes.arrayOf(PropTypes.element),
  /**
   * CSS classes for styling
   */
  className: PropTypes.string,
  /**
   * @deprecated
   * If the product is a pack or gift card, this is 'true' and renders a button that links directly to the PDP
   */
  showSeeFullDetails: PropTypes.bool,
  /**
   * If showSeeFullDetails is true, the link is passed into the component to navigate the user to a PDP
   */
  link: PropTypes.string,
  /**
   * Callback that forces emboss shadow
   */
  setForceEmbossShadow: PropTypes.func,
  /**
   * if showOptions is true, then size options will be displayed
   */
  showSizeOptions: PropTypes.bool,
  /**
   * Callback that sets the value for `showSizeOptions`
   */
  setShowSizeOptions: PropTypes.func,
  /**
   * Callback that opens the QuickAddModal component
   */
  openQuickAddModal: PropTypes.func,
  /**
   * The parent product's ID (e.g., externalSystems.shopify ID)
   */
  productId: PropTypes.string
};

QuickAdd.defaultProps = {
  children: [],
  className: null,
  showSeeFullDetails: false,
  link: null,
  setForceEmbossShadow: null,
  showSizeOptions: false,
  productId: null
};

export default QuickAdd;
